import { Lead, LeadStatus, StatusHistory } from '@otw/models/leads/lead'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { addDoc, collection, deleteDoc, deleteField, doc, getDoc, getDocs, Timestamp, updateDoc } from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'
import { filterLeadsByTruckCriteria } from 'views/SuperAdmin/Management/LeadManagement/util/truckFilterUtils'
import {
  syncContactsAfterLeadDeleted,
  syncContactsAfterLeadSaved,
} from 'views/SuperAdmin/Management/LeadManagement/util/contactLeadSync'
import { db } from '@otw/models/db'
import { allocateLeadNumber } from 'core/leads/allocateLeadNumber'
import { syncLeadDropoffToShipmentPlans } from 'core/logistics/syncLeadDropoffToShipmentPlans'

interface UseLeadsReturn {
  leads: Lead[] | undefined
  isLoading: boolean
  error: Error | null
  updateLead: ({
    id,
    updates,
    statusChangeNote,
  }: {
    id: string
    updates: Partial<Lead>
    statusChangeNote?: string
  }) => Promise<{ id: string } & Partial<Lead>>
  createLead: (
    lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt' | 'statusHistory' | 'prevStatus' | 'leadNumber'>,
  ) => Promise<Lead>
  deleteLead: (id: string) => Promise<string>
  getLeadsByAssignedRep: (repId: number) => Lead[] | undefined
  getLeadsByStatus: (status: LeadStatus) => Lead[] | undefined
  getLeadsByApplicationId: (applicationId: string) => Lead[] | undefined
  getLeadsByTruckCriteria: (criteria: {
    type?: string
    make?: string
    model?: string
    size?: string
    year?: number
  }) => Lead[] | undefined
}

type UseLeadsOptions = {
  /** When false, skip the full leads list query (mutations only). Default true. */
  fetchList?: boolean
}

export const useLeads = (options?: UseLeadsOptions): UseLeadsReturn => {
    const fetchList = options?.fetchList !== false
    const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()
  const queryKey = ['leads']

  const {
    data: leads,
    isLoading,
    error,
  } = useQuery<Lead[]>({
    queryKey,
    queryFn: async () => {
      const leadsRef = db.leads
      const snapshot = await getDocs(leadsRef)
      return snapshot.docs.map(doc => doc.data())
    },
    enabled: fetchList,
    staleTime: 1000 * 60 * 5,
  })

  const updateLeadMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
      statusChangeNote,
    }: {
      id: string
      updates: Partial<Lead>
      statusChangeNote?: string
    }) => {
      const leadRef = doc(db.leads, id)

      // Get the current lead data to track status changes
      const cachedLead = leads?.find(lead => lead.id === id)
      const currentLead =
        cachedLead ?? (await getDoc(leadRef)).data()
      const now = new Date()

      // Create status history entry if status is changing
      let statusHistory = currentLead?.statusHistory
        ? // Make a deep copy of the existing status history to preserve all properties
          JSON.parse(JSON.stringify(currentLead.statusHistory))
        : []

      if (updates.status && currentLead && updates.status !== currentLead.status) {
        // Create the status history entry with changedAt as an ISO string
        const historyEntry: Omit<StatusHistory, 'changedAt'> & { changedAt: string } = {
          id: uuidv4(),
          previousStatus: currentLead.status,
          newStatus: updates.status,
          changedBy: userInfo?.id || 'unknown',
          changedAt: now.toISOString(), // Store as ISO string
          notes: statusChangeNote,
        }

        // Add to status history
        statusHistory.push(historyEntry as any)

        // Update prevStatus
        updates.prevStatus = currentLead.status
      }

      // Ensure we properly handle the truck request data
      if (updates.truckRequest && currentLead?.truckRequest) {
        // Make sure we preserve the types array if it exists in the current lead
        if (currentLead.truckRequest.types && !updates.truckRequest.types) {
          updates.truckRequest.types = currentLead.truckRequest.types
        }

        // If we have truck types in the update, ensure they're properly formatted
        if (updates.truckRequest.types) {
          updates.truckRequest.types = updates.truckRequest.types.map(type => ({
            ...type,
            // Convert string budget to number if needed with proper type checking
            budget: typeof type.budget === 'string' ? Number(String(type.budget).replace(/,/g, '')) : type.budget,
          }))
        }
      }

      // Update lastActionDate when significant changes occur
      const significantChange = updates.status || updates.assignedRepId || updates.followUpNotes

      // Prepare the update data - always include updatedAt
      const { leadNumber: _ignoredLeadNumber, ...updatesWithoutLeadNumber } = updates
      const updateData = {
        ...updatesWithoutLeadNumber,
        updatedAt: now.toISOString(), // Ensure updatedAt is always set as ISO string
        ...(significantChange ? { lastActionDate: now.toISOString() } : {}),
      }

      // Always include statusHistory in the update if status is changing
      if (updates.status && currentLead && updates.status !== currentLead.status) {
        updateData.statusHistory = statusHistory
      }

      // Clean the update data to remove any undefined values
      const cleanedUpdateData = cleanUndefinedValues(updateData)

      await updateDoc(leadRef, cleanedUpdateData as any)

      const legacyFieldDeletes: Record<string, ReturnType<typeof deleteField>> = {}
      if (
        'truckDropoffLocationIds' in cleanedUpdateData ||
        'companyId' in cleanedUpdateData ||
        'dropoffLocationId' in cleanedUpdateData
      ) {
        legacyFieldDeletes.dropoffLocationId = deleteField()
        legacyFieldDeletes.dropoffLocation = deleteField()
      }
      if ('businessAddressId' in cleanedUpdateData) {
        legacyFieldDeletes.businessAddress = deleteField()
      }
      if (Object.keys(legacyFieldDeletes).length > 0) {
        await updateDoc(leadRef, legacyFieldDeletes)
      }

      // Return the updated lead with the new status history and updatedAt
      return {
        id,
        ...updates,
        updatedAt: now, // Include updatedAt in the return value
        statusHistory:
          updates.status && currentLead && updates.status !== currentLead.status ? statusHistory : undefined,
      }
    },
    onMutate: async ({ id, updates, statusChangeNote }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousLeads = queryClient.getQueryData<Lead[]>(queryKey)
      const now = new Date()

      // Update the local cache with the new status history and updatedAt
      queryClient.setQueryData<Lead[]>(queryKey, old => {
        if (!old) return old
        return old.map(lead => {
          if (lead.id === id) {
            // If status is changing, add a new status history entry
            if (updates.status && lead.status !== updates.status) {
              const newStatusHistory = lead.statusHistory ? [...lead.statusHistory] : []
              newStatusHistory.push({
                id: uuidv4(),
                previousStatus: lead.status,
                newStatus: updates.status,
                changedBy: userInfo?.id || 'unknown',
                changedAt: now,
                notes: statusChangeNote,
              })

              return {
                ...lead,
                ...updates,
                updatedAt: now, // Always update the updatedAt field
                statusHistory: newStatusHistory,
                prevStatus: lead.status,
              }
            }

            // Always update the updatedAt field even if status isn't changing
            return {
              ...lead,
              ...updates,
              updatedAt: now,
            }
          }
          return lead
        })
      })

      return { previousLeads }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLeads)
      showError('Error updating lead')
    },
    onSuccess: async (_data, variables) => {
      showSuccess('Lead updated successfully')
      const merged = queryClient.getQueryData<Lead[]>(queryKey)?.find((l) => l.id === variables.id)
      try {
        if (merged) {
          await syncContactsAfterLeadSaved(merged, queryClient)
        }
      } catch {
        showWarning('Lead saved, but updating the linked contact failed. Try refreshing contacts.')
      }
      if (
        variables.updates.status === 'closed' &&
        merged?.status === 'closed' &&
        merged.prevStatus !== 'closed'
      ) {
        try {
          await syncLeadDropoffToShipmentPlans(merged)
          queryClient.invalidateQueries({ queryKey: ['shipment_plans'] })
        } catch {
          showWarning(
            'Lead closed, but syncing dropoff locations to shipment plans failed. Try refreshing transporter management.',
          )
        }
      }
    },
  })

  const createLeadMutation = useMutation({
    mutationFn: async (
      lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt' | 'statusHistory' | 'prevStatus' | 'leadNumber'>,
    ) => {
      const leadsRef = db.leads
      const now = new Date()
      const leadNumber = await allocateLeadNumber(now)

      // Create initial status history entry
      const initialStatusHistory: StatusHistory = {
        id: uuidv4(),
        previousStatus: null,
        newStatus: lead.status || 'new',
        changedBy: userInfo?.id || 'unknown',
        changedAt: now,
        notes: 'Lead created',
      }

      const newLead = {
        ...lead,
        leadNumber,
        createdAt: now,
        updatedAt: now,
        lastActionDate: now,
        status: lead.status || 'new',
        prevStatus: null,
        truckIds: lead.truckIds || [],
        followUpNotes: lead.followUpNotes || [],
        statusHistory: [initialStatusHistory],
        applicationIds: lead.applicationIds || [],
        tags: lead.tags || [],
        createdBy: userInfo?.id || 'unknown',
      } as Lead

      const cleanedNewLead = cleanUndefinedValues(newLead) as Lead

      const docRef = await addDoc(leadsRef, cleanedNewLead)

      return {
        ...cleanedNewLead,
        id: docRef.id,
      } as Lead
    },
    onMutate: async newLead => {
      await queryClient.cancelQueries({ queryKey })
      const previousLeads = queryClient.getQueryData<Lead[]>(queryKey)

      const tempId = `temp-${Date.now()}`

      const optimisticLead = {
        ...newLead,
        id: tempId,
        createdAt: new Date(),
        updatedAt: new Date(),
        statusHistory: [
          {
            id: uuidv4(),
            previousStatus: null,
            newStatus: newLead.status || 'new',
            changedBy: userInfo?.id || 'unknown',
            changedAt: new Date(),
            notes: 'Lead created',
          },
        ],
        prevStatus: null,
      } as Lead

      queryClient.setQueryData<Lead[]>(queryKey, old => [...(old || []), optimisticLead])

      return { previousLeads, optimisticLead, tempId }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLeads)
      showError('Error creating lead')
    },
    onSuccess: async (result, variables, context) => {
      // Update the cache to replace the temporary ID with the real one
      if (context?.tempId) {
        queryClient.setQueryData<Lead[]>(queryKey, old => {
          if (!old) return [result]
          return old.map(lead => (lead.id === context.tempId ? result : lead))
        })
      }

      showSuccess('Lead created successfully')

      try {
        await syncContactsAfterLeadSaved(result, queryClient)
      } catch {
        showWarning('Lead created, but updating the linked contact failed. Try refreshing contacts.')
      }
    },
  })

  const deleteLeadMutation = useMutation({
    mutationFn: async (id: string) => {
      const leadRef = doc(db.leads, id)
      await deleteDoc(leadRef)
      return id
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      const previousLeads = queryClient.getQueryData<Lead[]>(queryKey)

      queryClient.setQueryData<Lead[]>(queryKey, old => {
        if (!old) return old
        return old.filter(lead => lead.id !== id)
      })

      return { previousLeads }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousLeads)
      showError('Error deleting lead')
    },
    onSuccess: async (deletedId: string) => {
      showSuccess('Lead deleted successfully')
      try {
        await syncContactsAfterLeadDeleted(deletedId, queryClient)
      } catch {
        showWarning('Lead deleted, but updating linked contacts failed. Try refreshing contacts.')
      }
    },
  })

  // Helper functions to filter leads
  const getLeadsByAssignedRep = (repId: number) => {
    return leads?.filter(lead => lead.assignedRepId === repId)
  }

  const getLeadsByStatus = (status: LeadStatus) => {
    return leads?.filter(lead => lead.status === status)
  }

  const getLeadsByApplicationId = (applicationId: string) => {
    return leads?.filter(lead => lead.applicationIds.includes(applicationId))
  }

  const getLeadsByTruckCriteria = (criteria: {
    type?: string
    make?: string
    model?: string
    size?: string
    year?: number
  }) => {
    if (!leads) return undefined
    return filterLeadsByTruckCriteria(leads, criteria)
  }

  return {
    leads,
    isLoading,
    error: error as Error | null,
    updateLead: updateLeadMutation.mutateAsync,
    createLead: createLeadMutation.mutateAsync,
    deleteLead: deleteLeadMutation.mutateAsync,
    getLeadsByAssignedRep,
    getLeadsByStatus,
    getLeadsByApplicationId,
    getLeadsByTruckCriteria,
  }
}

// Helper function to recursively remove undefined values from an object
function cleanUndefinedValues(obj: any): any {
  if (obj === undefined) return null
  if (obj === null) return null
  if (obj instanceof Date) return obj
  if (typeof obj !== 'object') return obj

  if (Array.isArray(obj)) {
    return obj.map(item => cleanUndefinedValues(item))
  }

  const cleanedObj: any = {}
  for (const key in obj) {
    if (obj[key] === undefined) {
      cleanedObj[key] = null
    } else if (typeof obj[key] === 'object') {
      cleanedObj[key] = cleanUndefinedValues(obj[key])
    } else {
      cleanedObj[key] = obj[key]
    }
  }

  return cleanedObj
}
